import { CSSProperties } from "react";
import Icon from "./Icon";
import { formatNumberToDisplayType, makeKey, styx } from "modules/Utils";
import { clsx } from "modules/Utils";
import Colors from "modules/Colors";
import Sty from "modules/Sty";
import ButtonSymphony from "./ButtonSymphony";

export interface StatItem {
    title: string | JSX.Element;
    value: number;
    displayType?: 'default' | 'currency' | 'percentage' | 'number';
    [key: string]: any;
}

interface StatProps {
    className: string;
    style?: CSSProperties;
    title?: string | number | JSX.Element | null; // NOTE: depends on isHorizontal
    subtitle?: string | number | JSX.Element | null;
    icon?: string;
    stats?: StatItem[];
    links?: Array<{ url: string; title: string }>;
    isBorder?: boolean;
    isHorizontal?: boolean;
    isReverse?: boolean;
    rightSideContent?: JSX.Element;
}

const Stat = ({
    className,
    style,
    icon,
    title,
    subtitle,
    stats: statsProps,
    links,
    isBorder = true,
    isHorizontal = false,
    isReverse = false,
    rightSideContent,
}: StatProps) => {
    let stats: StatItem[] = []

    if (isHorizontal) {
        stats = statsProps || [];
    } else {
        stats = statsProps || [{ title, value: 0, displayType: 'number' } as StatItem];
    }

    const getStatValue = (stat: StatItem) => {
        return formatNumberToDisplayType(stat.value, stat.displayType);
    }

    const renderStatInfo = () => {
        return stats.map((stat, index) => (
            <div
                key={makeKey('statItem', stat.title, index)}
                className="statItemContainer"
                style={styx(
                    sty.statItemContainer,
                    isHorizontal ? sty.horizontalStatItem : {},
                    isReverse && { flexDirection: 'column-reverse' }
                )}
            >
                <div className="statTitle" style={Sty.text14grey}>
                    {stat.title}
                </div>
                <div className="statValue" style={Sty.text20}>
                    {getStatValue(stat)}
                </div>
            </div>
        ))
    }

    const renderLinks = () => {
        if (!links || links.length === 0) {
            return null;
        }

        return (
            <div className="statLinksContainer" style={sty.linksContainer}>
                {links.map((link, index) => (
                    <ButtonSymphony
                        key={makeKey('statLink', link.url, index)}
                        className="statLinkButton"
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="link"
                        iconLeft="click"
                        iconLeftColor={Colors.purple}
                        iconLeftSize={14}
                        size="small"
                        style={sty.linkButton}
                    >
                        View Link
                    </ButtonSymphony>
                ))}
            </div>
        );
    }

    return (
        <div
            className={clsx(
                'statElement',
                className
            )}
            style={styx(
                sty.statElement,
                isBorder && sty.border,
                isHorizontal && sty.horizontal,
                style
            )}
        >
            {icon &&
                <Icon
                    className="statIcon"
                    style={sty.icon}
                    icon={icon}
                    size={40}
                />
            }
            {isHorizontal &&
                <div className="statMainTitle" style={{ ...Sty.text18, flex: 2 }}>
                    {title}
                    {subtitle &&
                        <div className="statSubtitle" style={Sty.text14grey}>
                            {subtitle}
                        </div>
                    }
                    {renderLinks()}

                </div>
            }
            {renderStatInfo()}
            {rightSideContent}
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    statElement: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        width: '100%',
        backgroundColor: 'white',
    },
    icon: {
        padding: 10,
        borderRadius: 8,
        backgroundColor: Colors.purpleLighter,
    },
    border: {
        borderWidth: 1,
        borderColor: Colors.greyLight,
        borderStyle: 'solid',
        borderRadius: 8,
        padding: 16,
    },
    horizontal: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    horizontalStatItem: {
        flex: 1,
    },
    statItemContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    linksContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        marginTop: 12,
    },
    linkButton: {
        alignSelf: 'flex-start',
        fontSize: 12,
    }
}

export default Stat;